import { faBroom, faEnvelope, faKey, faLock, faUserTag } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Dropdown, IconButton, Menu, MenuButton, MenuItem } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { EditIcon } from '../../../../design/static/icons/EditIcon';
import { IUserService } from '../../user/user.service';
import { IAdminUserInfo, IUserStore, UserRole } from '../../user/user.store';
import { ChangePasswordContainer } from './ChangePasswordContainer';
import { ChangeUserPlanContainer } from './ChangeUserPlanContainer';
import { ChangeUserRoleContainer } from './ChangeUserRoleContainer';

interface IProps {
  adminUserInfo: IAdminUserInfo;
  onResetClicked: (userId: string) => void;
}

export const ActionsContainer = observer((props: IProps): ReactElement => {
  const userService = useInjection(IUserService.$);
  const userStore = useInjection(IUserStore.$);
  const { t } = useTranslator();

  const [changeUserRoleModalShown, setChangeUserRoleModalShown] = useState(false);
  const [changeUserPlanModalShown, setChangeUserPlanModalShown] = useState(false);
  const [changePasswordModalShown, setChangePasswordModalShown] = useState(false);

  const handleAdminConfirmUser = async (userId: string): Promise<void> => {
    await userService.adminConfirmUser(userId);
  };

  const handleLockUser = async (userId: string, isLocked: boolean): Promise<void> => {
    await userService.lockUserAsync(userId, isLocked);
  };

  return (
    <>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{ root: { color: 'neutral' } }}
        >
          <FontAwesomeIcon
            icon='ellipsis-v'
            fixedWidth
          />
        </MenuButton>
        <Menu>
          {userStore.userInfo.id !== props.adminUserInfo.id && (
            <MenuItem
              onClick={(): void => {
                setChangeUserRoleModalShown(true);
              }}
            >
              <FontAwesomeIcon
                icon={faUserTag}
                fixedWidth
              />
              {t('change_user_role')}
            </MenuItem>
          )}
          <MenuItem
            onClick={(): void => {
              setChangePasswordModalShown(true);
            }}
          >
            <FontAwesomeIcon
              icon={faKey}
              color='primary'
              fixedWidth
            />
            {t('change_password')}
          </MenuItem>
          {props.adminUserInfo.role !== UserRole.Administrator && (
            <>
              <MenuItem
                onClick={(): void => {
                  setChangeUserPlanModalShown(true);
                }}
              >
                <EditIcon />
                {t('change_user_plan')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  void handleLockUser(props.adminUserInfo.id, props.adminUserInfo.isActive);
                }}
              >
                <FontAwesomeIcon
                  icon={faLock}
                  fixedWidth
                />
                {!props.adminUserInfo.isActive ? t('unlock_account') : t('lock_account')}
              </MenuItem>
            </>
          )}
          {!props.adminUserInfo.isEmailConfirmed && (
            <MenuItem
              onClick={() => {
                void handleAdminConfirmUser(props.adminUserInfo.id);
              }}
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                fixedWidth
              />
              {t('confirm_email')}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              props.onResetClicked(props.adminUserInfo.id);
            }}
          >
            <FontAwesomeIcon icon={faBroom} /> {t('reset_workspaces')}
          </MenuItem>
        </Menu>
      </Dropdown>
      <ChangeUserRoleContainer
        showModal={changeUserRoleModalShown}
        onClose={() => {
          setChangeUserRoleModalShown(false);
        }}
        userId={props.adminUserInfo.id}
      />
      <ChangeUserPlanContainer
        showModal={changeUserPlanModalShown}
        onClose={() => {
          setChangeUserPlanModalShown(false);
        }}
        userId={props.adminUserInfo.id}
      />
      <ChangePasswordContainer
        showModal={changePasswordModalShown}
        onClose={() => {
          setChangePasswordModalShown(false);
        }}
        userId={props.adminUserInfo.id}
      />
    </>
  );
});
